import { Component, computed, inject, input, Type } from '@angular/core';
import { MDDialogService, MDIconComponent, MDToolTipDirective } from '@melodic-ui/components';
import { APP_CONFIG, IAppSettings } from '../../config/interfaces/iapp-settings.interface';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';

/** The app an impersonation session opens in. Guardians and institution contacts use the learner app. */
export type ImpersonationTargetApp = 'learner' | 'instructor';

/**
 * Admin-side entry point into an impersonation session: confirms, then opens the target app with
 * `?userID=` in a new tab. The target app's `captureImpersonationTargetFromUrl` takes it from there.
 */
@Component({
	selector: 'lb-impersonate-button',
	standalone: true,
	imports: [MDIconComponent, MDToolTipDirective],
	templateUrl: './impersonate-button.component.html',
	styleUrl: './impersonate-button.component.scss'
})
export class ImpersonateButtonComponent {
	private _dialogService = inject(MDDialogService);
	private _config: IAppSettings = inject(APP_CONFIG);

	userID = input.required<number>();
	targetApp = input.required<ImpersonationTargetApp>();
	displayName = input<string>('');
	/** Role shown in the confirmation title, e.g. 'Learner', 'Instructor', 'Contact'. */
	roleLabel = input<string>('User');

	tooltip = computed(() => `Impersonate ${this.displayName()}`.trim());

	private _targetUrl = computed(() => {
		const baseUrl = this.targetApp() === 'instructor' ? this._config.instructorAppUrl : this._config.learnerAppUrl;
		return baseUrl ? `${baseUrl}?userID=${this.userID()}` : null;
	});

	impersonate(): void {
		const url = this._targetUrl();
		if (!url || !this.userID()) return;

		const dialogRef = this._dialogService.open(ConfirmationDialogComponent as Type<Component>, {
			title: `⚠️ Caution! Impersonating ${this.roleLabel()} ⚠️`,
			body: `You are about to impersonate ${this.displayName()} and act on their behalf. Please proceed with caution.`,
			icon: 'untitled-ui/users/users-02',
			successText: 'Continue'
		});

		dialogRef.afterClosed.subscribe((isConfirmed) => {
			if (isConfirmed) {
				window.open(url, '_blank');
			}
		});
	}
}
